import React, { useState, useEffect, useContext, useCallback } from 'react';
import useMount from 'ggApp/shared/hooks/useMount';
import PropTypes from 'prop-types';
import { RedirectFlow } from 'groverPaymentSDK';

import { LayoutComponent_LocalLoader as LocalLoader } from 'ggApp/modules/layout';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { mapPayment } from 'ggApp/shared/context/Order/helpers/orderMapper';
import { updateOrderStepState } from 'ggApp/shared/context/Order/services/tracking';
import { CHECKOUT_STEP_NAMES as STEP_NAMES } from 'ggApp/modules/newCheckout/constants';
import { OrderModes } from 'ggApp/shared/context/Order/types';

import Add from './Add';
import Select from './Select';

export const moveActivePaymentFirst = (paymentMethods, activePaymentIndex) => {
    if (activePaymentIndex === 0) return paymentMethods;

    const activePayment = paymentMethods.splice(activePaymentIndex, 1)[0];

    paymentMethods.unshift(activePayment);
    return paymentMethods;
};

const Payment = ({
    stepData,
    removeUrlParams,
    preflightActive,
    setPreflight,
    paymentTermsInfo,
    setPaymentTermsInfo,
}) => {
    const {
        cart,
        current,
        inEditMode,
        orderNumber,
        redirectionFlow,
        send,
        userPayments,
        orderMode,
    } = useContext(OrderContext);
    const [currentPaymentMethods, setCurrentPaymentMethods] = useState();
    const [addNewPaymentMode, setAddNewPaymentMode] = useState(false);
    const [isLoading, setIsLoading] = useState();

    useMount(() => {
        if (current.matches('paymentMethod')) {
            updateOrderStepState({
                orderMode,
                orderNumber,
                currentStep: STEP_NAMES.PAYMENT_METHODS,
            });
        }
        if (redirectionFlow.enabled && !redirectionFlow.success) {
            setAddNewPaymentMode(true);
        }
    });

    useEffect(() => {
        if (current.matches('paymentMethod')) {
            setAddNewPaymentMode(current.matches('paymentMethod.form'));
        }
    }, [current]);

    useEffect(() => {
        setIsLoading(true);

        if (Array.isArray(userPayments)) {
            let mappedPaymentResponse = userPayments.map(mapPayment);
            if (orderMode === OrderModes.Mix) {
                mappedPaymentResponse = mappedPaymentResponse.filter(
                    (paymentMethod) => paymentMethod.billingAccountId !== null,
                );
            }

            if (userPayments.length) {
                if (!cart.paymentMethod) {
                    setCurrentPaymentMethods(mappedPaymentResponse);
                } else {
                    const activePaymentIndex = mappedPaymentResponse.findIndex(
                        (pMethod) => pMethod.id === cart.paymentMethod.id,
                    );

                    const reorderedPaymentMethods = moveActivePaymentFirst(
                        mappedPaymentResponse,
                        activePaymentIndex,
                    );

                    setCurrentPaymentMethods(reorderedPaymentMethods);
                }
            }

            setIsLoading(false);
        }
    }, [cart, orderMode, userPayments]);

    const onAddedMethod = useCallback(
        ({ id, billingAccountId, type }) => {
            if (redirectionFlow.enabled) {
                removeUrlParams(Object.values(RedirectFlow));
                send('RESET_FROM_REDIRECT');
            }

            send('ADD', {
                orderNumber,
                paymentMethodId: id,
                billingAccountId,
                paymentMethodType: type,
            });
        },
        [redirectionFlow.enabled, send, orderNumber, removeUrlParams],
    );

    const onAddedLegacyMethod = useCallback(() => {
        send('ADDLEGACY');
    }, [send]);

    const handleBackButton = useCallback(() => {
        send('BACK');
        removeUrlParams(Object.values(RedirectFlow));
    }, [removeUrlParams, send]);

    return (
        <LocalLoader active={isLoading} className="checkoutModalLoader">
            {!addNewPaymentMode && currentPaymentMethods && currentPaymentMethods.length ? (
                <Select
                    currentPaymentMethods={currentPaymentMethods}
                    paymentMethod={cart.paymentMethod}
                />
            ) : (
                <Add
                    inEditMode={inEditMode}
                    stepData={stepData}
                    orderNumber={orderNumber}
                    handleBackButtonClick={handleBackButton}
                    onAdded={onAddedMethod}
                    onLegacyAdded={onAddedLegacyMethod}
                    preflightActive={preflightActive}
                    setPreflight={setPreflight}
                    paymentTermsInfo={paymentTermsInfo}
                    setPaymentTermsInfo={setPaymentTermsInfo}
                />
            )}
        </LocalLoader>
    );
};

Payment.propTypes = {
    stepData: PropTypes.shape({}).isRequired,
    removeUrlParams: PropTypes.func.isRequired,
};

export default Payment;
